// Strukturierte KI-Empfehlungen v2 (nur Admin): Claude erzeugt einzeln
// umsetzbare Vorschläge. Website-Vorschläge → Copy-Prompt für Claude Code im
// jeweiligen Site-Repo; PUBLIVO-Säulen → Ein-Klick-Übernahme (nachschärfen);
// jede Empfehlung hat einen Umgesetzt-Status, dessen Wirkung nach ~3–4 Wochen
// automatisch aus den GSC-Daten bewertet wird. Dazu: Wächter-Alerts + Monats-Report.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Sparkles, Loader2, Copy, Check, RotateCcw, AlertTriangle, TrendingDown, TrendingUp,
  Globe, Newspaper, Wrench, Bot, FileText, CheckCircle2, Ban, Timer, Megaphone,
  Plus, X, Mail,
} from 'lucide-react';
import toast from 'react-hot-toast';
import BerichtKachel from '@/components/BerichtKachel';
import {
  getRecommendations, generateRecommendations, patchRecommendation, applyPillar, applyAds,
  resolveAlert, listReports, generateReport,
  getCampaignDraft, generateCampaignDraft, applyCampaignDraft, getCampaignLandingpages, setDraftKeywords,
  getCampaignBriefing, pmaxNachruesten,
  formatModelName,
  type SeoRecommendationV2, type SeoAlert, type RecTarget,
} from '@/lib/analytics-api';

interface Props {
  projectId: string;
  projectName: string;
  from: string;
  to: string;
}

const targetMeta: Record<RecTarget, { label: string; icon: any; badge: string }> = {
  website: { label: 'Website', icon: Globe, badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  publivo: { label: 'PUBLIVO', icon: Newspaper, badge: 'bg-violet-50 text-violet-700 border-violet-200' },
  technik: { label: 'Technik', icon: Wrench, badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  ki: { label: 'KI-Suche', icon: Bot, badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  ads: { label: 'Google Ads', icon: Megaphone, badge: 'bg-rose-50 text-rose-700 border-rose-200' },
};

/**
 * Beschriftung + Sicherheitsabfrage für den GO-Button einer Ads-Empfehlung.
 * Die Abfrage nennt IMMER konkret, was im Konto passiert (Kampagne, Beträge) —
 * hier wird echtes Werbebudget bewegt. null = kein Button (nicht umsetzbar).
 */
function adsAction(rec: SeoRecommendationV2): { label: string; confirm: string } | null {
  const p: any = rec.apply_payload;
  if (!p) return null;
  switch (rec.rec_type) {
    case 'negative_keyword':
      return {
        label: 'Keyword ausschließen',
        confirm: `„${p.keyword_text}" (${p.match_type}) als NEGATIVES Keyword in Kampagne „${p.campaign_name}" einbuchen?`,
      };
    case 'keyword_neu':
      return {
        label: 'Keyword einbuchen',
        confirm: `„${p.keyword_text}" (${p.match_type}) als Keyword in Anzeigengruppe „${p.ad_group_name}" (Kampagne „${p.campaign_name}") einbuchen?`,
      };
    case 'budget': {
      const changes: any[] = Array.isArray(p.budget_changes) ? p.budget_changes : [];
      if (!changes.length) return null;
      const lines = changes.map((c) => `• ${c.campaign_name}: neu ${c.new_budget_eur} €/Tag`).join('\n');
      return {
        label: 'Budget übernehmen',
        confirm: `Tagesbudget im Google-Ads-Konto ändern?\n\n${lines}\n\nDie Kampagnen laufen danach mit diesen Beträgen weiter.`,
      };
    }
    case 'kampagne_pause':
      return {
        label: 'Kampagne pausieren',
        confirm: `Kampagne „${p.campaign_name}" jetzt PAUSIEREN? (Jederzeit im Konto wieder aktivierbar.)`,
      };
    case 'gebotsstrategie': {
      const changes: any[] = Array.isArray(p.strategie_changes) ? p.strategie_changes : [];
      if (!changes.length) return null;
      const name = (s: string) => (s === 'MAXIMIZE_CLICKS' ? 'Klicks maximieren' : 'Conversions maximieren');
      const lines = changes
        .map((c) => `• ${c.campaign_name} → ${name(c.strategy)}${c.cpc_limit_eur ? ` (CPC-Limit ${c.cpc_limit_eur} €)` : ''}`)
        .join('\n');
      return {
        label: 'Gebotsstrategie umstellen',
        confirm: `Gebotsstrategie im Google-Ads-Konto ändern?\n\n${lines}\n\nNach dem Wechsel beginnt eine Lernphase von 1–2 Wochen.`,
      };
    }
    case 'keyword_pause': {
      const teile = [
        p.pause !== false ? 'pausieren' : null,
        p.cpc_bid_eur ? `Gebot auf ${p.cpc_bid_eur} € setzen` : null,
      ].filter(Boolean);
      if (!teile.length) return null;
      return {
        label: p.pause !== false ? 'Keyword pausieren' : 'Gebot setzen',
        confirm: `Keyword „${p.keyword_text}" in Anzeigengruppe „${p.ad_group_name}" ${teile.join(' und ')}?`,
      };
    }
    case 'conversion_struktur': {
      const runter: any[] = Array.isArray(p.auf_sekundaer) ? p.auf_sekundaer : [];
      const rauf: any[] = Array.isArray(p.auf_primaer) ? p.auf_primaer : [];
      const ziele: any[] = Array.isArray(p.ziele_aus) ? p.ziele_aus : [];
      if (!runter.length && !rauf.length && !ziele.length) return null;
      // Jede einzelne Änderung nennen: Primär/Sekundär entscheidet, worauf
      // Google bietet — das darf niemand versehentlich umstellen.
      const lines = [
        ...rauf.map((c) => `• „${c.name}" → PRIMÄR (wird fürs Bieten genutzt)`),
        ...ziele.map(
          (z) =>
            `• Zielvorhaben ${z.paar} (${z.ebene === 'konto' ? 'Konto-Standard' : `Kampagne „${z.kampagne}"`}) → zählt nicht mehr fürs Bieten` +
            (Array.isArray(z.aktionen) && z.aktionen.length ? ` (betrifft ${z.aktionen.map((n: string) => `„${n}"`).join(', ')})` : '')
        ),
        ...runter.map((c) => `• „${c.name}" → sekundär (wird nur noch gezählt)`),
      ].join('\n');
      return {
        label: 'Conversion-Struktur umstellen',
        confirm:
          `Rollen der Conversion-Aktionen im Google-Ads-Konto ändern?\n\n${lines}\n\n` +
          `Sekundäre Aktionen bleiben im Reporting sichtbar, zählen aber nicht mehr fürs Bieten. ` +
          `Smart Bidding beginnt danach eine kurze Lernphase.`,
      };
    }
    default:
      return null;
  }
}

const verdictMeta: Record<string, { label: string; cls: string }> = {
  wirkt: { label: '✓ hat gewirkt', cls: 'bg-green-50 text-green-700 border-green-200' },
  keine_wirkung: { label: 'keine messbare Wirkung', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
  schlechter: { label: 'Entwicklung negativ', cls: 'bg-red-50 text-red-600 border-red-200' },
  zu_wenig_daten: { label: 'zu wenig Daten', cls: 'bg-gray-50 text-gray-400 border-gray-200' },
};

export default function SeoRecommendations({ projectId, from, to }: Props) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'open' | 'applied' | 'all'>('open');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['seo-recs-v2', projectId],
    queryFn: () => getRecommendations({ projectId }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['seo-recs-v2', projectId] });

  const genMut = useMutation({
    mutationFn: () => generateRecommendations({ projectId, from, to }),
    onSuccess: (resp) => {
      queryClient.setQueryData(['seo-recs-v2', projectId], resp);
      toast.success('Empfehlungen erzeugt');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'applied' | 'dismissed' | 'reopen' }) => patchRecommendation(id, action),
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e.message),
  });

  const pillarMut = useMutation({
    mutationFn: ({ id, confirmNew }: { id: string; confirmNew: boolean }) => applyPillar(id, confirmNew),
    onSuccess: () => { invalidate(); toast.success('In PUBLIVO übernommen'); },
    onError: (e: any) => toast.error(e.message),
  });

  const adsMut = useMutation({
    mutationFn: (id: string) => applyAds(id),
    onSuccess: () => { invalidate(); toast.success('In Google Ads übernommen'); },
    onError: (e: any) => toast.error(e.message),
  });

  const alertMut = useMutation({
    mutationFn: (id: string) => resolveAlert(id),
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e.message),
  });

  const copyPrompt = async (rec: SeoRecommendationV2) => {
    const text = rec.prompt || `${rec.title}\n\n${rec.proposal}\n\nDatengrundlage: ${rec.reason}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(rec.id);
      setTimeout(() => setCopiedId(null), 2500);
      toast.success('Prompt kopiert – in Claude Code im Website-Repo einfügen');
    } catch {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  const recs = data?.recommendations || [];
  const alerts = data?.alerts || [];
  const monatLabel = (ym: string) => {
    const [j, m] = ym.split('-').map(Number);
    return `${['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'][(m || 1) - 1]} ${String(j).slice(2)}`;
  };
  // Überschrift zeigt das AKTUELL konfigurierte Modell (kommt aus der API) —
  // beantwortet „womit rechnet die nächste Runde?". Nie hartcodieren.
  const modelLabel = formatModelName(data?.model);
  // Stammen die angezeigten Karten noch von einem älteren Modell, wird darauf
  // hingewiesen — sonst wirkt die Überschrift wie ein Widerspruch zu den Karten.
  const recsModel = [...recs].sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0]?.model;
  const staleModelLabel =
    recsModel && data?.model && recsModel !== data.model ? formatModelName(recsModel) : null;
  const visible = recs.filter((r) =>
    filter === 'all' ? true : filter === 'open' ? r.status === 'open' : r.status === 'applied'
  );
  const counts = {
    open: recs.filter((r) => r.status === 'open').length,
    applied: recs.filter((r) => r.status === 'applied').length,
  };

  return (
    <div className="space-y-4">
      {/* Wächter-Alerts */}
      {alerts.length > 0 && (
        <div className="bg-white rounded-lg border border-amber-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-amber-100 bg-amber-50/50 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <h2 className="text-sm font-semibold text-gray-900">Wächter-Meldungen</h2>
            <span className="text-xs text-gray-500">automatisch erkannt (nächtliche Prüfung)</span>
          </div>
          <ul className="divide-y divide-gray-50">
            {alerts.map((a: SeoAlert) => (
              <li key={a.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  {a.kind === 'rank_drop' ? <TrendingDown className="w-4 h-4 text-red-500 shrink-0" /> : <AlertTriangle className={`w-4 h-4 shrink-0 ${a.severity === 'critical' ? 'text-red-500' : 'text-amber-500'}`} />}
                  <span className="text-sm text-gray-800 truncate" title={a.title}>{a.title}</span>
                  <span className="text-[10px] text-gray-400 shrink-0">{new Date(a.created_at).toLocaleDateString('de-DE')}</span>
                </div>
                <button
                  onClick={() => alertMut.mutate(a.id)}
                  className="text-xs text-gray-400 hover:text-gray-700 shrink-0"
                >
                  Erledigt
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Suchtrends aus dem Keyword Planner — die Nachfrage, die die Seite
          noch nicht sieht. Steigende Themen ohne Säule/Seite werden von der
          Runde zu PUBLIVO- bzw. Onpage-Vorschlägen (als H2). */}
      {data?.suchtrends && (data.suchtrends.steigend.length > 0 || data.suchtrends.fallend.length > 0 || data.suchtrends.hinweis) && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <h2 className="text-sm font-semibold text-gray-900">Suchtrends</h2>
            <span className="text-xs text-gray-500">
              Keyword Planner{data.suchtrends.region ? ` · ${data.suchtrends.region}` : ''}
              {data.suchtrends.stand ? ` · Stand ${monatLabel(data.suchtrends.stand)}` : ''}
              {data.suchtrends.keywords_gesamt ? ` · ${data.suchtrends.keywords_gesamt} Begriffe` : ''}
            </span>
          </div>
          {data.suchtrends.hinweis && <p className="px-4 py-2 text-xs text-amber-700 bg-amber-50/60">{data.suchtrends.hinweis}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
            {([['steigend', 'Steigt', 'text-emerald-700'], ['fallend', 'Fällt', 'text-red-600']] as const).map(([key, label, farbe]) => (
              <div key={key}>
                <p className={`px-4 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wider ${farbe}`}>{label}</p>
                {data.suchtrends![key].length === 0 ? (
                  <p className="px-4 pb-3 text-xs text-gray-400">Kein Begriff über der Schwelle.</p>
                ) : (
                  <ul className="pb-2">
                    {data.suchtrends![key].map((t) => (
                      <li key={t.keyword} className="px-4 py-1 flex items-center gap-2 text-[13px]">
                        <span className="flex-1 truncate text-gray-800" title={t.keyword}>{t.keyword}</span>
                        <span className="text-gray-500 tabular-nums">{t.volumen}/Mon.</span>
                        <span className={`tabular-nums font-medium ${t.aenderung_prozent >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                          {t.aenderung_prozent >= 0 ? '+' : ''}{Math.round(t.aenderung_prozent)} %
                        </span>
                        {t.trend === 'saisonal' && <span className="text-[10px] text-gray-400">Saison</span>}
                        {t.vorjahr_prozent == null && <span className="text-[10px] text-gray-300" title="Vorjahr noch nicht prüfbar (weniger als 15 Monate Verlauf)">o. Vj.</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Kopf: Generieren + Filter */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-600" /> KI-Empfehlungen{modelLabel ? ` (${modelLabel})` : ''}
            </h2>
            <p className="text-xs text-gray-500 mt-1 max-w-xl">
              Aus Search Console, Besucher-Events, Lead-Qualität und PUBLIVO. Saisonbereinigt.
              Umgesetzte Empfehlungen werden nach ~3–4 Wochen automatisch auf Wirkung geprüft.
              {staleModelLabel && (
                <span className="block mt-1 text-amber-700">
                  Die angezeigten Empfehlungen stammen noch von {staleModelLabel} — für
                  {modelLabel ? ` ${modelLabel}` : ' das neue Modell'} eine neue Runde erzeugen.
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-xs">
              {([['open', `Offen (${counts.open})`], ['applied', `Umgesetzt (${counts.applied})`], ['all', 'Alle']] as Array<['open' | 'applied' | 'all', string]>).map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 font-medium rounded-md transition ${filter === k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                  {l}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                // Eine neue Runde ersetzt die offenen Empfehlungen — einmal
                // bestätigen, damit niemand versehentlich seine Liste ablöst.
                if (
                  counts.open > 0 &&
                  !window.confirm(
                    `Es stehen ${counts.open} offene Empfehlungen. Eine neue Runde ERSETZT sie — noch gültige Punkte nimmt die KI erneut auf, der Rest wandert unter „Alle" ins Archiv.\n\nBereits umgesetzte Empfehlungen bleiben unberührt.\n\nNeue Runde erzeugen?`
                  )
                ) {
                  return;
                }
                genMut.mutate();
              }}
              disabled={genMut.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-60"
            >
              {genMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {genMut.isPending ? 'Analysiere…' : 'Neue Runde erzeugen'}
            </button>
          </div>
        </div>
        {data?.summary && <p className="mt-3 text-sm text-gray-700 border-t border-gray-100 pt-3">{data.summary}</p>}
      </div>

      {/* Karten */}
      {isLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-sm text-gray-500">
          {recs.length === 0
            ? 'Noch keine Empfehlungen – oben „Neue Runde erzeugen" klicken.'
            : 'Nichts in dieser Ansicht.'}
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((rec) => {
            const meta = targetMeta[rec.target] || targetMeta.website;
            const Icon = meta.icon;
            const isPillar = rec.target === 'publivo' && (rec.rec_type === 'pillar_update' || rec.rec_type === 'pillar_new');
            const isNewPillar = rec.rec_type === 'pillar_new';
            const verdict = rec.evaluation?.verdict ? verdictMeta[rec.evaluation.verdict] : null;
            return (
              <div key={rec.id} className={`bg-white rounded-lg border p-4 ${rec.status === 'dismissed' ? 'border-gray-100 opacity-60' : 'border-gray-200'}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium ${meta.badge}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    {rec.priority === 'hoch' && <span className="px-2 py-0.5 rounded-md bg-red-50 text-red-600 border border-red-200 text-[11px] font-medium">hohe Prio</span>}
                    {isNewPillar && (
                      <span className="px-2 py-0.5 rounded-md bg-orange-50 text-orange-700 border border-orange-200 text-[11px] font-medium">
                        ⚠ Neue Säule (Ausnahme)
                      </span>
                    )}
                    {rec.status === 'applied' && !verdict && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 text-blue-600 border border-blue-200 text-[11px]">
                        <Timer className="w-3 h-3" /> umgesetzt {rec.applied_at ? new Date(rec.applied_at).toLocaleDateString('de-DE') : ''} · Wirkung wird beobachtet
                      </span>
                    )}
                    {verdict && (
                      <span className={`px-2 py-0.5 rounded-md border text-[11px] font-medium ${verdict.cls}`}>{verdict.label}</span>
                    )}
                  </div>
                  <span className="text-[10px] text-gray-300">{new Date(rec.created_at).toLocaleDateString('de-DE')}</span>
                </div>

                <h3 className="mt-2 text-sm font-semibold text-gray-900">{rec.title}</h3>
                <p className="mt-1 text-xs text-gray-500"><strong className="text-gray-600">Daten:</strong> {rec.reason}</p>
                <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{rec.proposal}</p>
                {(rec.page || rec.query) && (
                  <p className="mt-1.5 text-[11px] text-gray-400">
                    {rec.page && <span className="font-mono">{rec.page}</span>}
                    {rec.page && rec.query && ' · '}
                    {rec.query && <>Query: „{rec.query}"</>}
                  </p>
                )}

                {/* Aktionen */}
                {rec.status !== 'dismissed' && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {/* Copy-Prompt für alles, was im Code passiert (website,
                        technik, ki) — Backend liefert für diese Ziele einen
                        fertigen Arbeitsauftrag in rec.prompt. */}
                    {rec.prompt && rec.status === 'open' && (
                      <button
                        onClick={() => copyPrompt(rec)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-xs font-medium"
                      >
                        {copiedId === rec.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                        {copiedId === rec.id ? 'Kopiert!' : 'Prompt für Claude Code kopieren'}
                      </button>
                    )}
                    {rec.target === 'ads' && rec.apply_payload && rec.status === 'open' && (() => {
                      const a = adsAction(rec);
                      if (!a) return null;
                      return (
                        <button
                          onClick={() => { if (window.confirm(a.confirm)) adsMut.mutate(rec.id); }}
                          disabled={adsMut.isPending}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium disabled:opacity-60"
                          title={a.confirm}
                        >
                          {adsMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Megaphone className="w-3.5 h-3.5" />}
                          {a.label}
                        </button>
                      );
                    })()}
                    {isPillar && rec.status === 'open' && (
                      <button
                        onClick={() => {
                          if (isNewPillar && !window.confirm('Wirklich eine NEUE Themensäule in PUBLIVO anlegen? (Standard ist Nachschärfen – neue Säulen nur bei echter Lücke.)')) return;
                          pillarMut.mutate({ id: rec.id, confirmNew: isNewPillar });
                        }}
                        disabled={pillarMut.isPending}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium disabled:opacity-60"
                      >
                        {pillarMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Newspaper className="w-3.5 h-3.5" />}
                        {isNewPillar ? 'Neue Säule in PUBLIVO anlegen' : 'Säule in PUBLIVO nachschärfen'}
                      </button>
                    )}
                    {rec.status === 'open' && !isPillar && (
                      <button
                        onClick={() => patchMut.mutate({ id: rec.id, action: 'applied' })}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-300 text-green-700 hover:bg-green-50 text-xs font-medium"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Umgesetzt
                      </button>
                    )}
                    {rec.status === 'open' && (
                      <button
                        onClick={() => patchMut.mutate({ id: rec.id, action: 'dismissed' })}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 text-xs"
                      >
                        <Ban className="w-3.5 h-3.5" /> Verwerfen
                      </button>
                    )}
                    {rec.status === 'applied' && (
                      <button
                        onClick={() => patchMut.mutate({ id: rec.id, action: 'reopen' })}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 text-xs"
                        title="Zurück auf offen (Bewertung wird verworfen)"
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Doch nicht
                      </button>
                    )}
                  </div>
                )}
                {rec.status === 'dismissed' && (
                  <button onClick={() => patchMut.mutate({ id: rec.id, action: 'reopen' })} className="mt-2 text-xs text-gray-400 hover:text-gray-700 inline-flex items-center gap-1">
                    <RotateCcw className="w-3 h-3" /> Wieder öffnen
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Kampagnen-Generator */}

      {/* Monats-Report */}
    </div>
  );
}

// ── Kampagnen-Generator ──────────────────────────────────────────────────────

// Wird auch vom eigenen Google-Ads-Tab in AnalyticsPage gerendert — der
// Generator ist ein eigenstaendiges Werkzeug und nicht Teil der Empfehlungen.
export function CampaignBuilderPanel({
  projectId,
  seiteImFokus,
  onFokusAufheben,
}: {
  projectId: string;
  /** Aus der Landingpage-Liste angestoßen: für DIESE Seite bauen. */
  seiteImFokus?: string | null;
  onFokusAufheben?: () => void;
}) {
  const queryClient = useQueryClient();
  const [budget, setBudget] = useState('20');
  // Leer starten: Der Ort wird aus dem Steckbrief des Projekts vorbefüllt,
  // sobald die Seiten geladen sind (siehe useEffect unten).
  const [ort, setOrt] = useState('');
  const [umkreis, setUmkreis] = useState('40');
  // Bundesweit-Projekte (z. B. Nicos Eigenmarketing): ganz Deutschland statt
  // Ort + Umkreis — die Geo-Felder entfallen dann.
  const [bundesweit, setBundesweit] = useState(false);
  // Landingpage-Auswahl: welche Seiten der Website dieser Entwurf bewirbt.
  // Abgewählte URLs merken (Default = alles gewählt, auch wenn neue dazukommen).
  const [abgewaehlt, setAbgewaehlt] = useState<Record<string, boolean>>({});
  const [manuelleUrl, setManuelleUrl] = useState('');
  // Rückfrage-Antwort: Abweichungen von der KI-Empfehlung (id → an/aus)
  const [fokusSel, setFokusSel] = useState<Record<string, boolean>>({});

  const { data } = useQuery({ queryKey: ['campaign-draft', projectId], queryFn: () => getCampaignDraft(projectId) });
  const draft = data?.draft || null;
  // Deklarierte Landingpages (Ads-Manifest /nlt-ads.json bzw. nlt-angles-Meta)
  const { data: lpData } = useQuery({ queryKey: ['campaign-lps', projectId], queryFn: () => getCampaignLandingpages(projectId) });
  const seiten = lpData?.seiten || [];
  // Kommt der Anstoß aus der Landingpage-Liste ("Kampagne für diese Seite"),
  // ist die Auswahl genau diese eine Seite — die Frage "für welche Seite baue
  // ich hier eigentlich?" soll gar nicht erst aufkommen.
  const gewaehlteSeiten = seiteImFokus
    ? seiten.filter((s) => s.url === seiteImFokus)
    : seiten.filter((s) => !abgewaehlt[s.url]);
  const fokusSeite = seiteImFokus ? seiten.find((s) => s.url === seiteImFokus) || null : null;

  // Ort aus dem Unternehmens-Steckbrief vorbefüllen. Nur, solange Nico das Feld
  // nicht selbst angefasst hat — eine getippte Eingabe wird nie überschrieben.
  useEffect(() => {
    const vorschlag = lpData?.ort_vorschlag;
    if (vorschlag && !ort.trim()) setOrt(vorschlag);
    // Absichtlich nur am Vorschlag hängen: sonst würde jede Tastatureingabe
    // den Effekt erneut auslösen und den Wert zurücksetzen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lpData?.ort_vorschlag]);
  const rueckfrage = draft?.draft.rueckfrage || null;
  const istRueckfrage = !!rueckfrage && (draft?.draft.campaigns || []).length === 0 && draft?.status === 'entwurf';
  const fokusChecked = (id: string) => fokusSel[id] ?? (rueckfrage?.empfehlung || []).includes(id);

  const genMut = useMutation({
    mutationFn: (vars: { fokus?: string[] }) => {
      const auswahl = [
        ...gewaehlteSeiten.map((s) => s.url),
        ...(manuelleUrl.trim() ? [manuelleUrl.trim()] : []),
      ];
      // Auswahl mitschicken, wenn sie vom Default (= alle Seiten) abweicht oder
      // gezielt für eine Seite gebaut wird. Sonst entscheidet die KI über alle.
      const abweichend = !!seiteImFokus || manuelleUrl.trim() !== '' || gewaehlteSeiten.length !== seiten.length;
      return generateCampaignDraft(projectId, {
        budget_eur_pro_tag: parseFloat(budget) || 10,
        ort: ort.trim(),
        umkreis_km: parseInt(umkreis) || 40,
        ...(bundesweit ? { geo_modus: 'bundesweit' as const } : {}),
        ...(abweichend && auswahl.length ? { landingpages_auswahl: auswahl } : {}),
        ...(vars.fokus?.length ? { fokus_auswahl: vars.fokus } : {}),
      });
    },
    onSuccess: () => {
      setFokusSel({});
      queryClient.invalidateQueries({ queryKey: ['campaign-draft', projectId] });
      toast.success('Kampagnen-Entwurf erstellt');
    },
    onError: (e: any) => toast.error(e.message),
  });
  const applyMut = useMutation({
    mutationFn: () => applyCampaignDraft(projectId, draft!.id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['campaign-draft', projectId] }); toast.success('Kampagnen PAUSIERT in Google Ads angelegt'); },
    onError: (e: any) => toast.error(e.message),
  });

  // Begleit-PMax nachrüsten (Viertel-Regel) — für Projekte, deren Suchkampagnen
  // schon angelegt wurden, bevor die PMax zum Standard-Baustein wurde.
  // Idempotent: Hat das Konto bereits eine PMax, meldet der Server das nur.
  const pmaxBudgetVorschlag = Math.max(1, Math.round((Number(draft?.params?.budget_eur_pro_tag) || 10) * 0.25 * 2) / 2);
  const pmaxMut = useMutation({
    mutationFn: () => pmaxNachruesten(projectId, draft?.draft.pmax?.tagesbudget_eur || pmaxBudgetVorschlag),
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-draft', projectId] });
      if (d.pmax?.uebersprungen) toast(d.pmax.grund || 'Konto hat bereits eine PMax — nichts angelegt', { icon: 'ℹ️' });
      else if (d.pmax?.kampagne) toast.success(`„${d.pmax.kampagne}" PAUSIERT angelegt (${d.pmax.tagesbudget_eur} €/Tag)`);
      else toast.error(d.pmax?.error || 'PMax-Anlage fehlgeschlagen');
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Briefing für den Kunden: kurzer Text zum Herauskopieren in die Mail.
  const [briefing, setBriefing] = useState<string | null>(null);
  const [briefingKopiert, setBriefingKopiert] = useState(false);
  const briefingMut = useMutation({
    mutationFn: (neu: boolean) => getCampaignBriefing(projectId, draft!.id, neu),
    onSuccess: (d) => { setBriefing(d.briefing); setBriefingKopiert(false); },
    onError: (e: any) => toast.error(e.message),
  });

  // Keywords einer Anzeigengruppe ändern (nachbuchen / entfernen), bevor die
  // Kampagne im Konto landet.
  const kwMut = useMutation({
    mutationFn: (v: { ci: number; gi: number; keywords: Array<{ text: string; match_type: string }> }) =>
      setDraftKeywords({ projectId, draftId: draft!.id, campaignIndex: v.ci, adGroupIndex: v.gi, keywords: v.keywords }),
    onSuccess: (d) => queryClient.setQueryData(['campaign-draft', projectId], { draft: d.draft }),
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-rose-600" /> Schritt 2 · Kampagne entwerfen
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Entwirft komplette Kampagnen nach der bewährten Blaupause und legt sie auf Knopfdruck{' '}
          <strong>pausiert</strong> im Konto an — live schaltest du nach Sichtprüfung selbst.
        </p>
      </div>

      {/* ── Für welche Seite? Steht VOR den Vorgaben und vor dem Knopf, damit
          die Frage gar nicht erst offen bleibt. ── */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/60">
        <p className="text-[11px] font-medium text-gray-500 mb-1.5">
          Beworbene Seite
          {lpData?.quelle === 'lms' && <span className="text-gray-400"> · im Dashboard festgelegt</span>}
          {lpData?.quelle === 'manifest' && <span className="text-gray-400"> · aus /nlt-ads.json der Website</span>}
          {lpData?.quelle === 'angles-meta' && <span className="text-gray-400"> · Angles aus nlt-angles-Meta</span>}
        </p>

        {fokusSeite ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-800">
              {fokusSeite.dienstleistung || fokusSeite.url.replace(/^https?:\/\/[^/]+/, '') || fokusSeite.url}
              {fokusSeite.angles.length > 0 && (
                <span className="font-normal text-rose-600">· {fokusSeite.angles.length} Angles</span>
              )}
            </span>
            <button
              onClick={() => onFokusAufheben?.()}
              className="text-[11px] text-gray-500 underline hover:text-gray-700"
            >
              andere Seite wählen
            </button>
          </div>
        ) : seiten.length > 0 ? (
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {seiten.map((s) => (
              <label key={s.url} className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!abgewaehlt[s.url]}
                  onChange={(e) => setAbgewaehlt((prev) => ({ ...prev, [s.url]: !e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <span>
                  {s.dienstleistung || s.url.replace(/^https?:\/\/[^/]+/, '') || s.url}
                  {s.angles.length > 0 && <span className="text-gray-400"> ({s.angles.length} Angles)</span>}
                </span>
              </label>
            ))}
            <input
              value={manuelleUrl}
              onChange={(e) => setManuelleUrl(e.target.value)}
              placeholder="Weitere Unterseite (URL/Pfad, optional)"
              className="w-64 px-2 py-1 border border-gray-300 rounded-lg text-xs"
            />
          </div>
        ) : (
          <p className="text-xs text-amber-700">
            Keine Seite festgelegt — oben unter „Beworbene Landingpages" eintragen. Sonst wird nur die Startseite
            analysiert.
          </p>
        )}
      </div>

      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="block text-[10px] text-gray-400 mb-0.5">Budget €/Tag ges.</label>
            <input value={budget} onChange={(e) => setBudget(e.target.value)} inputMode="decimal" className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-[10px] text-gray-400 mb-0.5">Zielgebiet</label>
            <label className="inline-flex items-center gap-1.5 px-2 py-1.5 border border-gray-300 rounded-lg text-sm cursor-pointer select-none bg-white">
              <input type="checkbox" checked={bundesweit} onChange={(e) => setBundesweit(e.target.checked)} className="rounded border-gray-300" />
              Bundesweit
            </label>
          </div>
          {!bundesweit && (
            <>
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Ort</label>
                <input value={ort} onChange={(e) => setOrt(e.target.value)} placeholder="aus Steckbrief" className="w-32 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 mb-0.5">Umkreis km</label>
                <input value={umkreis} onChange={(e) => setUmkreis(e.target.value)} inputMode="numeric" className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
              </div>
            </>
          )}
          <button
            onClick={() => {
              if (!bundesweit && !ort.trim()) { toast.error('Bitte Ort angeben'); return; }
              if (seiten.length > 0 && gewaehlteSeiten.length === 0 && !manuelleUrl.trim()) {
                toast.error('Bitte mindestens eine Landingpage wählen');
                return;
              }
              genMut.mutate({});
            }}
            disabled={genMut.isPending}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium disabled:opacity-60"
          >
            {genMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {genMut.isPending ? 'Entwerfe…' : 'Entwurf erstellen'}
          </button>
        </div>
      </div>

      {draft && (
        <div className="px-4 py-4 space-y-4 bg-gray-50/40">
          {/* ── Rückfrage: Budget trägt nicht alles → NICO wählt den Fokus ── */}
          {istRueckfrage && rueckfrage && (
            <div className="border border-amber-300 bg-amber-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-900">Deine Entscheidung ist gefragt</p>
              <p className="text-xs text-amber-800 mt-1">{rueckfrage.frage}</p>
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {rueckfrage.optionen.map((o) => {
                  const checked = fokusChecked(o.id);
                  const empfohlen = (rueckfrage.empfehlung || []).includes(o.id);
                  return (
                    <label
                      key={o.id}
                      className={`block border rounded-lg p-2.5 cursor-pointer bg-white transition ${checked ? 'border-amber-400 ring-1 ring-amber-300' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <span className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setFokusSel((prev) => ({ ...prev, [o.id]: !checked }))}
                          className="rounded border-gray-300"
                        />
                        <span className="text-xs font-semibold text-gray-900">{o.label}</span>
                        {empfohlen && (
                          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-medium">KI-Empfehlung</span>
                        )}
                      </span>
                      {o.begruendung && <span className="block text-[11px] text-gray-500 mt-1 ml-6">{o.begruendung}</span>}
                    </label>
                  );
                })}
              </div>
              <button
                onClick={() => {
                  const fokus = rueckfrage.optionen.filter((o) => fokusChecked(o.id)).map((o) => o.id);
                  if (!fokus.length) { toast.error('Bitte mindestens eine Option wählen'); return; }
                  genMut.mutate({ fokus });
                }}
                disabled={genMut.isPending}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium disabled:opacity-60"
              >
                {genMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {genMut.isPending ? 'Entwerfe…' : `Mit diesem Fokus entwerfen (${rueckfrage.optionen.filter((o) => fokusChecked(o.id)).length} gewählt)`}
              </button>
            </div>
          )}

          {/* ── Analyse-Karten ── */}
          <div className="grid lg:grid-cols-2 gap-3">
            {draft.draft.angle_check && (
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider mb-1">Angle-Check</p>
                <p className="text-xs text-gray-600 leading-relaxed">{draft.draft.angle_check}</p>
              </div>
            )}
            {draft.draft.budget_einschaetzung && (
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Budget-Einschätzung</p>
                <p className="text-xs text-gray-600 leading-relaxed">{draft.draft.budget_einschaetzung}</p>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium border ${draft.draft.keyword_recherche === 'keyword-planner' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
              {draft.draft.keyword_recherche === 'keyword-planner'
                ? '✓ Keyword-Recherche: Google Keyword Planner (echte Ø-Suchvolumina, regional)'
                : 'Keyword-Recherche: ohne Volumendaten'}
            </span>
            {draft.params?.fokus_auswahl?.length ? (
              <span className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                Fokus: {draft.params.fokus_auswahl.join(', ')}
              </span>
            ) : null}
          </div>
          {(draft.draft.lp_vorschlaege || []).length > 0 && (
            <div className="text-xs bg-violet-50 border border-violet-200 rounded-lg p-3">
              <p className="font-semibold text-violet-800 mb-1">Landingpage-Vorschläge (erst LP erweitern, dann diese Intention bewerben):</p>
              {(draft.draft.lp_vorschlaege || []).map((v, i) => <p key={i} className="text-violet-700">• {v}</p>)}
            </div>
          )}
          {(draft.draft.hinweise || []).map((h, i) => (
            <p key={i} className="text-xs text-amber-700">• {h}</p>
          ))}

          {/* ── Kampagnen-Karten (Google-Ads-Struktur: Kampagne → Anzeigengruppen) ── */}
          {draft.draft.campaigns.map((c, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-[10px] font-semibold uppercase tracking-wide shrink-0">Kampagne</span>
                  <span className="text-sm font-semibold text-gray-900 truncate" title={c.name}>{c.name}</span>
                </div>
                <div className="text-xs text-gray-500 shrink-0">
                  {c.tagesbudget_eur} €/Tag · {c.ad_groups.length} Anzeigengruppen · Suche ·{' '}
                  {c.phase?.start_strategie || 'Klicks maximieren'}
                </div>
              </div>

              {/* ── Phasen-Rechnung: was dieses Budget in dieser Region kauft ──
                  Kein Kundenklassen-Urteil, sondern Klickpreis × Nachfrage ×
                  Conversion-Rate. Sagt auch, wohin die Kampagne laufen kann. */}
              {c.phase && (
                <div className="px-4 py-2.5 border-b border-gray-200 bg-blue-50/40">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600">
                    {c.phase.klicks_pro_monat != null && (
                      <span>
                        <strong className="text-gray-900">{new Intl.NumberFormat('de-DE').format(c.phase.klicks_pro_monat)}</strong> Klicks/Mon.
                        {c.phase.nachfrage_geschaetzt && (
                          <span
                            className="text-amber-700"
                            title="Nachfrage grob geschätzt und eher eine Untergrenze — der Planner liefert das Volumen für den Ort, nicht für den ganzen Umkreis."
                          >
                            {' '}(Nachfrage geschätzt)
                          </span>
                        )}
                      </span>
                    )}
                    {c.phase.cpc_eur != null && (
                      <span>
                        Klickpreis <strong className="text-gray-900">{c.phase.cpc_eur.toFixed(2)} €</strong>
                        <span className="text-gray-400"> ({c.phase.cpc_quelle === 'konto' ? 'Konto' : 'Planner'})</span>
                      </span>
                    )}
                    {c.phase.conversions_pro_monat != null && (
                      <span>
                        ≈ <strong className="text-gray-900">{c.phase.conversions_pro_monat}</strong> Conversions/Mon.
                      </span>
                    )}
                    <span className="text-gray-400">
                      Ziel: {c.phase.ziel_strategie}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] leading-relaxed text-gray-500">{c.phase.begruendung}</p>
                </div>
              )}
              <div className="divide-y divide-gray-100">
                {c.ad_groups.map((g, j) => (
                  <details key={j} className="group">
                    <summary className="px-4 py-2.5 cursor-pointer flex items-center justify-between gap-3 hover:bg-gray-50">
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-gray-800">
                          <span className="text-gray-300">↳</span>{g.name}
                          {g.typ === 'umfeld' && (
                            <span
                              className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
                              title="Angrenzende Suchintention: umfasst die Leistung, benennt sie aber nicht. Eigenes Gebot, eigener Anzeigentext, getrennt auswertbar."
                            >
                              Umfeld{g.cpc_gebot_eur ? ` · ${g.cpc_gebot_eur.toFixed(2)} € Gebot` : ''}
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] text-gray-400 truncate">{g.final_url}</p>
                      </div>
                      <span className="text-[11px] text-gray-400 shrink-0">{g.keywords.length} Keywords · {g.headlines.length} Headlines · aufklappen</span>
                    </summary>
                    <div className="px-4 pb-4 pt-1 grid lg:grid-cols-2 gap-4 bg-gray-50/50">
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Keywords</p>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-[10px] text-gray-400 uppercase">
                              <th className="py-1 font-medium">Keyword</th>
                              <th className="py-1 font-medium">Match</th>
                              <th className="py-1 font-medium text-right">Ø Suchvol./Mon.</th>
                              <th className="py-1 font-medium text-right">Klickpreis</th>
                              <th className="py-1" />
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {g.keywords.map((k, ki) => (
                              <tr key={ki} className="group/kw">
                                <td className="py-1 text-gray-800">{k.text}</td>
                                <td className="py-1">
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${k.match_type === 'EXACT' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                    {k.match_type === 'EXACT' ? 'Exakt' : 'Passende Wortgr.'}
                                  </span>
                                </td>
                                <td className="py-1 text-right tabular-nums text-gray-600">
                                  {k.suchvolumen_monat != null ? (
                                    new Intl.NumberFormat('de-DE').format(k.suchvolumen_monat)
                                  ) : k.longtail ? (
                                    // Kein Planner-Treffer ist hier eine ABSICHT, keine Lücke:
                                    // Longtails fangen die seltene Anfrage mit hoher Absicht ab.
                                    <span
                                      className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 text-[10px] font-medium"
                                      title="Bewusst gesetzter Longtail — kein ausgewiesenes Volumen, kostet nichts, solange niemand sucht"
                                    >
                                      Longtail
                                    </span>
                                  ) : (
                                    <span className="text-gray-300">–</span>
                                  )}
                                </td>
                                <td className="py-1 text-right tabular-nums text-gray-500">
                                  {k.cpc_min_eur != null && k.cpc_max_eur != null
                                    ? `${k.cpc_min_eur.toFixed(2)}–${k.cpc_max_eur.toFixed(2)} €`
                                    : <span className="text-gray-300">–</span>}
                                </td>
                                <td className="py-1 pl-1 text-right">
                                  {draft.status === 'entwurf' && (
                                    <button
                                      onClick={() =>
                                        kwMut.mutate({
                                          ci: i,
                                          gi: j,
                                          keywords: g.keywords.filter((_, x) => x !== ki).map((x) => ({ text: x.text, match_type: x.match_type })),
                                        })
                                      }
                                      disabled={kwMut.isPending}
                                      title="Keyword aus dieser Anzeigengruppe entfernen"
                                      className="rounded p-0.5 text-gray-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* ── Recherchiert, aber nicht gebucht ──────────────────
                            Der Planner liefert bis zu 120 Begriffe, in die
                            Anzeigengruppen schaffen es ~30. Die restlichen sind
                            bezahlte Information — hier stehen sie, statt
                            weggeworfen zu werden, und lassen sich einzeln
                            nachbuchen. */}
                        {(() => {
                          const gebucht = new Set(
                            c.ad_groups.flatMap((x) => x.keywords.map((k) => k.text.toLowerCase()))
                          );
                          const offen = (draft.draft.keyword_planner?.ideen || []).filter(
                            (idee) => !gebucht.has(idee.text.toLowerCase())
                          );
                          if (!offen.length) return null;
                          return (
                            <details className="mt-3">
                              <summary className="cursor-pointer text-[10px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600">
                                Recherchiert, nicht gebucht ({offen.length}) — aufklappen
                              </summary>
                              <div className="mt-1.5 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                                <table className="w-full text-xs">
                                  <tbody className="divide-y divide-gray-100">
                                    {offen.map((idee, ii) => (
                                      <tr key={ii}>
                                        <td className="py-1 pl-2 text-gray-700">{idee.text}</td>
                                        <td className="py-1 text-right tabular-nums text-gray-500">
                                          {idee.suchvolumen_monat != null
                                            ? new Intl.NumberFormat('de-DE').format(idee.suchvolumen_monat)
                                            : '–'}
                                        </td>
                                        <td className="py-1 pl-2 text-right tabular-nums text-gray-400">
                                          {idee.cpc_min_eur != null && idee.cpc_max_eur != null
                                            ? `${idee.cpc_min_eur.toFixed(2)}–${idee.cpc_max_eur.toFixed(2)} €`
                                            : '–'}
                                        </td>
                                        <td className="py-1 pr-2 pl-1 text-right">
                                          {draft.status === 'entwurf' && (
                                            <div className="inline-flex gap-1">
                                              {(['PHRASE', 'EXACT'] as const).map((mt) => (
                                                <button
                                                  key={mt}
                                                  onClick={() =>
                                                    kwMut.mutate({
                                                      ci: i,
                                                      gi: j,
                                                      keywords: [
                                                        ...g.keywords.map((x) => ({ text: x.text, match_type: x.match_type })),
                                                        { text: idee.text, match_type: mt },
                                                      ],
                                                    })
                                                  }
                                                  disabled={kwMut.isPending || g.keywords.length >= 25}
                                                  title={`In „${g.name}" aufnehmen (${mt === 'EXACT' ? 'Exakt' : 'Passende Wortgruppe'})`}
                                                  className="inline-flex items-center gap-0.5 rounded border border-gray-300 px-1 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-40"
                                                >
                                                  <Plus className="h-2.5 w-2.5" />
                                                  {mt === 'EXACT' ? 'Ex' : 'Ph'}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {g.keywords.length >= 25 && (
                                <p className="mt-1 text-[10px] text-amber-700">
                                  25 Keywords erreicht — erst eines entfernen, dann nachbuchen.
                                </p>
                              )}
                            </details>
                          );
                        })()}
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Anzeigen-Vorschau (responsive Suchanzeige)</p>
                        <div className="border border-gray-200 rounded-lg p-3 bg-white">
                          <p className="text-[11px] text-gray-500">
                            <span className="font-bold text-gray-700">Anzeige</span> · {g.final_url.replace(/^https?:\/\//, '').split('?')[0]}
                          </p>
                          <p className="text-[#1a0dab] text-sm font-medium leading-snug mt-0.5">{g.headlines.slice(0, 3).join(' | ')}</p>
                          <p className="text-xs text-gray-700 mt-1 leading-relaxed">{g.descriptions.slice(0, 2).join(' ')}</p>
                        </div>
                        {g.headlines.length > 3 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {g.headlines.slice(3).map((h, hi) => (
                              <span key={hi} className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px]">{h}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </details>
                ))}
              </div>
              {(c.negative_keywords || []).length > 0 && (
                <div className="px-4 py-2.5 border-t border-gray-100 bg-rose-50/50">
                  <p className="text-[10px] font-semibold text-rose-600 uppercase tracking-wider mb-1">Ausgeschlossene Suchbegriffe (Intent-Schutz)</p>
                  <div className="flex flex-wrap gap-1">
                    {(c.negative_keywords || []).map((k, ki) => (
                      <span key={ki} className="px-1.5 py-0.5 rounded bg-white border border-rose-200 text-rose-700 text-[10px]">− {k.text}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Begleit-PMax (Viertel-Regel): fester Bestandteil des Anlegens —
              hier nur sichtbar gemacht, damit die Budget-Rechnung aufgeht. */}
          {draft.draft.pmax && draft.draft.campaigns.length > 0 && (
            <div className="border border-violet-200 bg-violet-50/50 rounded-xl px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-violet-900">{draft.draft.pmax.name}</p>
                  <p className="text-[11px] text-violet-700 mt-0.5">
                    Begleit-Netz (Performance Max) — wird beim Anlegen automatisch miterstellt: Texte aus der Suchkampagne, Bilder von der Landingpage, pausiert.
                  </p>
                </div>
                <span className="px-2 py-1 rounded-lg bg-white border border-violet-200 text-violet-800 text-xs font-medium">
                  {draft.draft.pmax.tagesbudget_eur} €/Tag (25 %)
                </span>
              </div>
            </div>
          )}

          {/* Briefing zum Herauskopieren. Bewusst ein einfaches Textfeld statt
              formatiertem Text: Der Inhalt soll unverändert in eine Mail. */}
          {briefing !== null && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 p-4" onClick={() => setBriefing(null)}>
              <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Briefing für den Kunden</p>
                    <p className="text-[11px] text-gray-500">Zum Kopieren in deine E-Mail — ohne Anrede und Grußformel.</p>
                  </div>
                  <button onClick={() => setBriefing(null)} aria-label="Schließen" className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <textarea
                  value={briefing}
                  onChange={(e) => setBriefing(e.target.value)}
                  rows={9}
                  className="w-full resize-y border-0 px-5 py-4 text-sm leading-relaxed text-gray-800 outline-none"
                />
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-5 py-3">
                  <button
                    onClick={() => briefingMut.mutate(true)}
                    disabled={briefingMut.isPending}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                  >
                    {briefingMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                    Neu formulieren
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(briefing);
                        setBriefingKopiert(true);
                        setTimeout(() => setBriefingKopiert(false), 2000);
                      } catch {
                        toast.error('Kopieren nicht möglich — Text bitte von Hand markieren');
                      }
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-1.5 text-xs font-medium text-white hover:bg-gray-800"
                  >
                    {briefingKopiert ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {briefingKopiert ? 'Kopiert' : 'Text kopieren'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Das Briefing bleibt verfügbar, auch nachdem die Kampagnen im Konto
              liegen — verschickt wird es ja meist erst dann. */}
          {draft.draft.campaigns.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {draft.status === 'entwurf' && (
                <button
                  onClick={() => { if (window.confirm(`${draft.draft.campaigns.length} Suchkampagne(n)${draft.draft.pmax ? ' + 1 Begleit-PMax' : ''} PAUSIERT im Google-Ads-Konto anlegen?`)) applyMut.mutate(); }}
                  disabled={applyMut.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium disabled:opacity-60"
                >
                  {applyMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
                  {applyMut.isPending ? 'Lege an…' : 'In Google Ads anlegen (pausiert)'}
                </button>
              )}
              <button
                onClick={() => briefingMut.mutate(false)}
                disabled={briefingMut.isPending}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                {briefingMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Briefing für den Kunden
              </button>
              {draft.status === 'angelegt' && (
                <button
                  onClick={() => { if (window.confirm(`Begleit-PMax (${draft.draft.pmax?.tagesbudget_eur || pmaxBudgetVorschlag} €/Tag, Viertel-Regel) PAUSIERT anlegen? Hat das Konto schon eine PMax, passiert nichts.`)) pmaxMut.mutate(); }}
                  disabled={pmaxMut.isPending}
                  className="inline-flex items-center gap-2 rounded-lg border border-violet-300 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-60"
                >
                  {pmaxMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
                  Begleit-PMax nachrüsten
                </button>
              )}
            </div>
          )}

          {draft.status === 'angelegt' ? (
            <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
              ✓ Angelegt am {draft.applied_at ? new Date(draft.applied_at).toLocaleString('de-DE') : ''} — im Ads-Konto prüfen und live schalten.
              {Array.isArray(draft.result) && draft.result.some((r: any) => r.conversion_aktionen) && (
                <span className="block mt-1">
                  Conversion-Aktionen: {Object.entries(draft.result.find((r: any) => r.conversion_aktionen)!.conversion_aktionen as Record<string, string>)
                    .map(([k, v]) => `${k} → ${v}`)
                    .join(' · ')}
                </span>
              )}
              {Array.isArray(draft.result) && draft.result.some((r: any) => r.warnings?.length || r.error) && (
                <span className="block mt-1 text-amber-700">Hinweise: {draft.result.flatMap((r: any) => [...(r.warnings || []), ...(r.error ? [r.error] : [])]).join(' · ')}</span>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ── Monats-Report ────────────────────────────────────────────────────────────

function monatLabel(ym: string): string {
  const d = new Date(`${ym.slice(0, 7)}-01T00:00:00Z`);
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function lastFullMonth(): string {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
}

// Wird auch vom eigenen Monatsbericht-Tab in AnalyticsPage gerendert.
export function MonthlyReportPanel({ customerId, customerName }: { customerId: string; customerName: string }) {
  const queryClient = useQueryClient();
  const [month, setMonth] = useState<string>(lastFullMonth());

  const { data: listData, isLoading } = useQuery({
    queryKey: ['monthly-report-list', customerId],
    queryFn: () => listReports(customerId),
  });
  const berichte = listData?.reports || [];
  const berichteAb = listData?.berichte_ab || null;
  // Stichtag aus der API: Liegt der letzte volle Monat davor, springt die
  // Auswahl auf den ersten Berichtsmonat, statt einen gesperrten anzubieten.
  useEffect(() => {
    if (berichteAb && month < berichteAb) setMonth(berichteAb);
  }, [berichteAb, month]);
  const ersterBerichtNochOffen = !!berichteAb && lastFullMonth() < berichteAb;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['monthly-report-list', customerId] });

  const genMut = useMutation({
    mutationFn: () => generateReport(customerId, month),
    onSuccess: () => { invalidate(); toast.success('Monatsbericht erstellt'); },
    onError: (e: any) => toast.error(e.message),
  });

  const schonDa = berichte.some((r) => r.month.slice(0, 7) === month);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <FileText className="h-4 w-4 text-blue-600" /> Monatsberichte — {customerName}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            Ein Bericht je Monat über alle Pakete des Kunden. Der Monatslauf am 1. legt ihn als Entwurf ab.
            {ersterBerichtNochOffen && (
              <span className="block mt-0.5 text-amber-700">
                Berichte starten mit {monatLabel(berichteAb!)} — der erste Entwurf kommt am 1. des Folgemonats.
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="month"
            value={month}
            min={berichteAb || undefined}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
          />
          <button
            onClick={() => {
              if (schonDa && !window.confirm(`Für ${month} gibt es schon einen Bericht. Neu erstellen und den bisherigen Text ersetzen?`)) return;
              genMut.mutate();
            }}
            disabled={genMut.isPending || !month}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {genMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
            {genMut.isPending ? 'Erstelle…' : schonDa ? 'Neu erstellen' : 'Bericht erstellen'}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-gray-300" /></div>
      ) : berichte.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-xs text-gray-400">
          Noch kein Bericht für diesen Kunden. Monat oben wählen und erstellen.
        </div>
      ) : (
        <div className="grid gap-4 rounded-lg border border-gray-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-3">
          {berichte.map((r) => (
            <BerichtKachel
              key={r.id}
              zeigeKunde={false}
              invalidate={invalidate}
              b={{
                id: r.id,
                month: r.month,
                created_at: r.created_at,
                released_at: r.released_at || null,
                customer_id: customerId,
                kunde: customerName,
                report_email: listData?.report_email || null,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
